import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useUiStore } from "../../../stores/uiStore";

/**
 * 把頁面標題(與選用的返回鍵)發佈到 topbar —— topbar 是唯一的頁面標題。
 * 詳情頁/表單頁沒有 PageHeader,也要用這個,否則 topbar 會空一塊。
 *
 *   usePageTitle("使用者編輯", onCancel)   // 標題左邊出現返回箭頭
 *   usePageTitle("使用者管理")             // 列表頁,沒有箭頭
 *
 * 返回鍵放 topbar 是 Figma v2(2128:23479)的版面:箭頭與標題同一群組,
 * 內容區不再另起一列「← 返回列表」(否則標題會出現兩次)。
 */
export function usePageTitle(title, onBack) {
  const setPageTitle = useUiStore((s) => s.setPageTitle);
  const setPageBack = useUiStore((s) => s.setPageBack);

  // onBack 通常是行內箭頭函式,每次 render 都是新的 reference。
  // 放進 ref,effect 只跟著「標題」與「有沒有返回鍵」跑,不會每 render 重設 store。
  const backRef = useRef(onBack);
  backRef.current = onBack;
  const hasBack = typeof onBack === "function";

  useEffect(() => {
    if (typeof title === "string") setPageTitle(title);
    setPageBack(hasBack ? () => backRef.current && backRef.current() : null);
    return () => {
      setPageTitle("");
      setPageBack(null);
    };
  }, [title, hasBack, setPageTitle, setPageBack]);
}

/**
 * 頁首 design system primitive。
 *
 * Figma(DCK-00 page con,18:704)把頁面標題放在 topbar 的白卡裡,內容區不再有一層頁首,
 * 所以這裡只把標題「發佈」給 topbar,版面上僅渲染右側動作區。
 * breadcrumbs 設計稿沒有 → 不再渲染,參數保留相容既有呼叫端。
 *
 *   <PageHeader title="機房管理" actions={<Button icon="mdi-plus">新增</Button>} />
 */
export const PageHeader = ({ title, breadcrumbs, actions }) => {
  usePageTitle(title);

  return actions ? (
    <div className="d-flex justify-content-end gap-2 mb-3 flex-wrap">{actions}</div>
  ) : null;
};
PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  // 設計稿無麵包屑;保留 prop 以相容既有呼叫端,不渲染
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, to: PropTypes.string })
  ),
  actions: PropTypes.node,
};

export default PageHeader;
